/**
 * Tabul „Sinteză": perioada pe un ecran.
 *
 * Șase cifre pe care antreprenorul le cere primele, graficul pe bucket-uri,
 * cei mai mari clienți și cele mai mari cheltuieli, plus tot ce e de verificat
 * — avertismentele adunate din toate secțiunile, ca să nu fie nevoie să intri
 * în fiecare tab ca să afli că lipsește un extras sau un fluturaș.
 */
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import type { MonthlyReport } from '../../../lib/monthlyReportApi';
import { fmtDate, fmtMoney, fmtPct } from '../../../lib/monthlyReportFormat';
import { useI18n } from '../../../contexts/I18nContext';
import { ChangeHint, PartyList, ReportSection, Stat } from './ReportSections';

export function SynthesisSection({ report }: { report: MonthlyReport }) {
  const { t, locale } = useI18n();
  const s = report.summary;
  const people = report.people;
  const marginPct = s.inflows > 0 ? Math.round((s.net / s.inflows) * 1000) / 10 : null;

  // Toate avertismentele perioadei, într-un singur loc.
  const warnings: string[] = [
    ...report.dataQuality.warnings.map((w) =>
      t(`monthlyReport.quality.warning.${w}` as 'monthlyReport.quality.warning.no_statements'),
    ),
    ...(people?.warnings ?? []).map((w) =>
      t(`monthlyReport.people.warning.${w}` as 'monthlyReport.people.warning.payroll_missing'),
    ),
    ...(report.projects.warnings ?? [])
      .filter((w) => w !== 'labor_estimated')
      .map((w) =>
        w === 'hours_without_rate'
          ? t('monthlyReport.projects.warning.hours_without_rate', {
              n: report.projects.hoursWithoutRateTotal ?? 0,
            })
          : t(
              `monthlyReport.projects.warning.${w}` as 'monthlyReport.projects.warning.budget_other_currency',
            ),
      ),
  ];

  return (
    <>
      <ReportSection title={t('monthlyReport.section.synthesis')}>
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-3">
          <Stat
            label={t('monthlyReport.summary.inflows')}
            value={fmtMoney(s.inflows, 'RON', locale)}
            tone="good"
            hint={
              <ChangeHint
                change={s.change.inflows}
                fallback={t('monthlyReport.summary.noPrevious')}
              />
            }
          />
          <Stat
            label={t('monthlyReport.summary.outflows')}
            value={fmtMoney(s.outflows, 'RON', locale)}
            tone="bad"
            hint={
              <ChangeHint
                change={s.change.outflows}
                fallback={t('monthlyReport.summary.noPrevious')}
              />
            }
          />
          <Stat
            label={t('monthlyReport.summary.net')}
            value={fmtMoney(s.net, 'RON', locale)}
            tone={s.net >= 0 ? 'good' : 'bad'}
            hint={
              <ChangeHint change={s.change.net} fallback={t('monthlyReport.summary.noPrevious')} />
            }
          />
          <Stat
            label={t('monthlyReport.synthesis.marginPct')}
            value={marginPct === null ? '—' : fmtPct(marginPct)}
            tone={marginPct === null ? 'neutral' : marginPct >= 0 ? 'good' : 'bad'}
          />
          <Stat
            label={t('monthlyReport.synthesis.peopleCost')}
            value={people ? fmtMoney(people.totalCost, 'RON', locale) : '—'}
            hint={
              people && people.shareOfOutflows !== null
                ? `${t('monthlyReport.people.shareOfOutflows')}: ${fmtPct(people.shareOfOutflows)}`
                : undefined
            }
          />
          <Stat
            label={t('monthlyReport.summary.bankBalance')}
            value={
              s.bankBalance === null
                ? t('monthlyReport.summary.bankBalanceUnknown')
                : fmtMoney(s.bankBalance, 'RON', locale)
            }
            hint={
              s.bankBalanceAsOf
                ? t('monthlyReport.summary.bankBalanceAsOf', {
                    date: fmtDate(s.bankBalanceAsOf, locale),
                  })
                : undefined
            }
          />
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-1 text-sm sm:grid-cols-3">
          <div className="flex justify-between">
            <dt className="text-gray-600">{t('monthlyReport.synthesis.burn')}</dt>
            <dd className="tabular-nums text-gray-900">
              {fmtMoney(report.trends.averageExpenses3m, 'RON', locale)}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600">{t('monthlyReport.trends.runway')}</dt>
            <dd className="tabular-nums text-gray-900">
              {report.trends.runwayMonths === null
                ? t('monthlyReport.trends.runwayUnknown')
                : t('monthlyReport.trends.runwayValue', { n: report.trends.runwayMonths })}
            </dd>
          </div>
          <div className="flex justify-between">
            <dt className="text-gray-600">{t('monthlyReport.summary.invoicedVsCollected')}</dt>
            <dd className="tabular-nums text-gray-900">
              {fmtMoney(s.invoiced, 'RON', locale)} / {fmtMoney(s.collected, 'RON', locale)}
            </dd>
          </div>
        </dl>
      </ReportSection>

      <ReportSection title={t('monthlyReport.synthesis.attention')}>
        {warnings.length === 0 ? (
          <p className="flex items-center gap-2 text-sm text-green-700">
            <CheckCircle2 className="h-4 w-4" />
            {t('monthlyReport.synthesis.allGood')}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {warnings.map((w) => (
              <li key={w} className="flex items-start gap-2 text-sm text-gray-700">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" />
                <span>{w}</span>
              </li>
            ))}
          </ul>
        )}
      </ReportSection>

      <ReportSection title={t('monthlyReport.synthesis.topClients')}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <PartyList
            title={t('monthlyReport.revenue.topInvoiced')}
            rows={report.revenue.topClientsInvoiced.slice(0, 3)}
          />
          <PartyList
            title={t('monthlyReport.synthesis.topExpenses')}
            rows={report.expenses.categories.slice(0, 3).map((c) => ({
              name: c.label,
              amount: c.amount,
              count: 1,
              share: c.share,
            }))}
          />
        </div>
      </ReportSection>
    </>
  );
}
